import { useQuery } from "@tanstack/react-query";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import api from "@/lib/api";
import { GlassCard, Kpi, Chip, Skeleton, tip, AV_FALLBACK, PageHead, Bar } from "@/v2/admin/ui";
import { useDept } from "./DeptShell";
import {
  Users, UserCheck, CheckCircle2, Gauge, TrendingUp, Trophy, Activity, Coins,
  Flame, Award, LayoutDashboard, CalendarCheck, Sparkles, Star,
} from "lucide-react";

const KIND_ICON = { challenge: CheckCircle2, badge: Award, level: Star, streak: Flame };

export default function DeptOverview() {
  const { q } = useDept();
  const { data: s } = useQuery({ queryKey: ["dept-ov", q], queryFn: async () => (await api.get(`/dept/overview?${q}`)).data });
  const { data: prog } = useQuery({ queryKey: ["dept-prog", q], queryFn: async () => (await api.get(`/dept/progress?${q}`)).data });
  const { data: ach } = useQuery({ queryKey: ["dept-ach", q], queryFn: async () => (await api.get(`/dept/achievements?${q}`)).data });

  if (!s) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="space-y-6" data-testid="dept-overview">
      <PageHead icon={LayoutDashboard} title="لوحة الإدارة" sub={`نظرة شاملة على أداء موظفي إدارة ${s.department}.`} />

      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4">
        <Kpi icon={Users} label="موظفو الإدارة" value={s.employees_count} testId="dkpi-employees" />
        <Kpi icon={UserCheck} label="حضور اليوم" value={s.active_today} />
        <Kpi icon={Activity} label="متفاعلون اليوم" value={s.participants_today} tone="gold" />
        <Kpi icon={CheckCircle2} label="تحديات مكتملة" value={s.completed_challenges} />
        <Kpi icon={CalendarCheck} label="إنجازات هذا الأسبوع" value={s.week_challenges} tone="gold" />
        <Kpi icon={TrendingUp} label="نسبة الإنجاز" value={s.completion_rate} suffix="%" />
        <Kpi icon={Gauge} label="متوسط الدرجات" value={s.avg_score} suffix="%" />
        <Kpi icon={Trophy} label="نسبة النجاح" value={s.pass_rate} suffix="%" />
        <Kpi icon={Coins} label="متوسط النقاط" value={s.avg_points} tone="gold" />
        <Kpi icon={Flame} label="متوسط سلسلة التعلّم" value={s.avg_streak} tone="red" />
        <Kpi icon={Award} label="شارات الإدارة" value={s.badges_count} tone="gold" />
        <Kpi icon={Sparkles} label="المشاركة الشهرية" value={s.monthly_participation} suffix="%" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <GlassCard className="p-5 lg:col-span-2">
          <h3 className="font-bold mb-4">تفاعل الإدارة خلال آخر 14 يومًا</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={s.series}>
              <defs>
                <linearGradient id="dg1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#91cab8" stopOpacity={0.5} /><stop offset="100%" stopColor="#91cab8" stopOpacity={0} /></linearGradient>
                <linearGradient id="dg2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#bb9661" stopOpacity={0.4} /><stop offset="100%" stopColor="#bb9661" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="day" tick={{ fill: "#bcc8cf", fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fill: "#bcc8cf", fontSize: 11 }} />
              <Tooltip {...tip} />
              <Area name="حضور" dataKey="checkins" stroke="#91cab8" strokeWidth={2} fill="url(#dg1)" />
              <Area name="محاولات" dataKey="attempts" stroke="#bb9661" strokeWidth={2} fill="url(#dg2)" />
            </AreaChart>
          </ResponsiveContainer>
        </GlassCard>

        <GlassCard className="p-5">
          <h3 className="font-bold mb-3 flex items-center gap-2"><Trophy size={16} className="text-[#e3c799]" /> أفضل موظفي الإدارة</h3>
          <ul className="space-y-2" data-testid="dept-top">
            {(s.top_employees || []).map((t, i) => (
              <li key={t.id} className="flex items-center gap-3">
                <span className="w-6 font-bold v2-gold-text">{i + 1}</span>
                <img src={t.avatar} alt={t.name} onError={AV_FALLBACK} className="w-9 h-9 rounded-full object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{t.name}</div>
                  <div className="text-[10px] text-[#bcc8cf]">{t.completed} تحديًا مكتملًا</div>
                </div>
                <span className="font-extrabold v2-emerald-text">{t.points}</span>
              </li>
            ))}
            {(s.top_employees || []).length === 0 && <p className="text-sm text-[#bcc8cf]">لا يوجد موظفون بعد.</p>}
          </ul>
        </GlassCard>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <GlassCard className="p-5">
          <h3 className="font-bold mb-4">تقدّم الإدارة في التحديات</h3>
          <div className="space-y-3 max-h-80 overflow-y-auto pe-1" data-testid="dept-progress">
            {(prog?.rows || []).map((r) => (
              <div key={r.id || r.day}>
                <div className="flex justify-between text-[11px] text-[#bcc8cf] mb-1">
                  <span className="truncate max-w-[70%]">اليوم {r.day} — {r.title}</span>
                  <span>{r.completed}/{prog.employees} · {r.pct}%</span>
                </div>
                <Bar value={r.pct} />
              </div>
            ))}
            {(prog?.rows || []).length === 0 && <p className="text-sm text-[#bcc8cf]">لا توجد تحديات مفعّلة.</p>}
          </div>
        </GlassCard>

        <GlassCard className="p-5">
          <h3 className="font-bold mb-4 flex items-center gap-2"><Sparkles size={16} className="text-[#e3c799]" /> إنجازات الإدارة الأخيرة</h3>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {[["هذا الأسبوع", ach?.week_completed || 0], ["هذا الشهر", ach?.month_completed || 0], ["إجمالي النقاط", ach?.total_points || 0]].map(([l, v]) => (
              <div key={l} className="v2-inset p-3 text-center">
                <div className="text-lg font-extrabold v2-gold-text">{v}</div>
                <div className="text-[10px] text-[#bcc8cf]">{l}</div>
              </div>
            ))}
          </div>
          <div className="space-y-2 max-h-56 overflow-y-auto pe-1" data-testid="dept-achievements">
            {(ach?.feed || []).slice(0, 15).map((f, i) => {
              const I = KIND_ICON[f.kind] || Sparkles;
              return (
                <div key={i} className="flex items-center gap-3 v2-inset p-2.5">
                  <span className="w-8 h-8 rounded-full bg-[#91cab8]/15 text-[#91cab8] flex items-center justify-center shrink-0"><I size={15} /></span>
                  <div className="flex-1 min-w-0 text-xs">
                    <span className="font-bold">{f.employee}</span> <span className="text-[#bcc8cf]">{f.text}</span>
                  </div>
                </div>
              );
            })}
            {(ach?.feed || []).length === 0 && <p className="text-sm text-[#bcc8cf]">لا توجد إنجازات بعد.</p>}
          </div>
          {(ach?.streak_milestones || []).length > 0 && (
            <div className="flex gap-2 flex-wrap mt-4">
              {ach.streak_milestones.map((m) => (
                <Chip key={m.name} tone="red"><Flame size={12} /> {m.name} — {m.streak} أيام</Chip>
              ))}
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}
